import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { useTheme } from './ThemeContext';
import { isAdminEmail } from './adminApi';
import CallHistory from './CallHistory';
import './Navbar.css';

interface NavbarProps {
  user: User | null;
  onSignOut: () => void;
}

function Navbar({ user, onSignOut }: NavbarProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const userInitial = user?.email ? user.email.charAt(0).toUpperCase() : '?';

  const handleOverlayClick = () => {
    setPanelOpen(false);
    setDropdownOpen(false);
  };

  const handleDropdownItemClick = (action: () => void) => {
    setDropdownOpen(false);
    setPanelOpen(false);
    action();
  };

  return (
    <>
      <nav className="navbar">
        {user && (
          <button
            className="hamburger-button"
            onClick={() => setPanelOpen(!panelOpen)}
            aria-label="Menu"
          >
            <span className="hamburger-line hamburger-line-top"></span>
            <span className="hamburger-line hamburger-line-bottom"></span>
          </button>
        )}
        <div className="navbar-brand"><span className="brand-mark" aria-hidden="true"></span>Callout</div>
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
        </button>
      </nav>

      {/* Overlay */}
      {panelOpen && (
        <div className="panel-overlay" onClick={handleOverlayClick}></div>
      )}

      {/* Side Panel */}
      {user && (
        <div className={`side-panel ${panelOpen ? 'side-panel-open' : ''}`}>
          <div className="side-panel-content">
            <button
              className="panel-nav-item"
              onClick={() => {
                setPanelOpen(false);
                navigate('/');
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              New Call
            </button>
            <CallHistory open={panelOpen} onNavigate={() => setPanelOpen(false)} />
          </div>
          
          {/* User Avatar at Bottom */}
          <div className="side-panel-footer">
            <button
              className="avatar-button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              title={user.email || undefined}
            >
              {userInitial}
            </button>
          </div>
        </div>
      )}

      {/* Dropdown Menu - Bottom Right Position (outside side panel for proper z-index) */}
      {user && dropdownOpen && (
        <div className="dropdown-menu dropdown-menu-bottom-right" ref={dropdownRef}>
          <div className="dropdown-header">{user.email}</div>
          <div className="dropdown-divider" />
          <button
            className="dropdown-item"
            onClick={() => handleDropdownItemClick(() => navigate('/plans'))}
          >
            Plans
          </button>
          <button
            className="dropdown-item"
            onClick={() => handleDropdownItemClick(() => navigate('/developer'))}
          >
            Developer
          </button>
          {isAdminEmail(user.email) && (
            <button
              className="dropdown-item"
              onClick={() => handleDropdownItemClick(() => navigate('/admin'))}
            >
              Admin
            </button>
          )}
          <button
            className="dropdown-item"
            onClick={() => handleDropdownItemClick(() => onSignOut())}
          >
            Sign Out
          </button>
        </div>
      )}
    </>
  );
}

export default Navbar;
